import { Button } from "@/components/ui/button";
import useActiveIndustryData from "@/module/hook/useActiveIndustryData";
import { PlusCircle, IndianRupee, Wallet, TrendingUp } from "lucide-react";
import AcademyAccordion from "./AcademyAccordion";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useAppSelector } from "@/stores/hook";
import useProgramVariants from "@/features/academy/api/useProgramVariants";
import { getAcademyModuleEntry } from "@/module/academy/helpers/useAcademyChildModuleData";
import { useMemo } from "react";

const fmt = (v: number) =>
  v.toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function AcademyChildOverview() {
  const { smartRoute } = useActiveIndustryData();
  const { lead } = useActiveDataMutation();
  const { currentChildsOnSelectedStage } = useChildForm();
  const { stageId } = useAppSelector((state) => state.childFunctional);
  const { variants } = useProgramVariants();

  const handleAddDeal = () => {
    smartRoute();
  };

  const disabled = stageId !== lead?.stage?.id;

  const stageInsights = useMemo(() => {
    if (!currentChildsOnSelectedStage?.length) {
      return {
        totalValue: 0,
        received: 0,
        pending: 0,
        totalDeals: 0,
        byVariant: [] as any[],
      };
    }

    let totalValue = 0;
    let received = 0;
    let pending = 0;

    const variantCountMap = new Map<
      number,
      { name: string; count: number; subtotal: number }
    >();

    for (const c of currentChildsOnSelectedStage) {
      const net = c.netAmount ?? c.totalDealValue ?? 0;
      totalValue += net;
      received += c.totalPaid ?? 0;
      pending += c.pendingAmount ?? 0;

      const entry = getAcademyModuleEntry(c);
      const vId = entry?.id;
      if (vId != null) {
        const existing = variantCountMap.get(vId);
        const vName =
          variants?.find((x: any) => x.id === vId)?.name ??
          entry?.value ??
          "Unknown";
        if (existing) {
          existing.count += 1;
          existing.subtotal += net;
        } else {
          variantCountMap.set(vId, { name: vName, count: 1, subtotal: net });
        }
      }
    }

    const byVariant = Array.from(variantCountMap.values());

    return {
      totalValue,
      received,
      pending,
      totalDeals: currentChildsOnSelectedStage.length,
      byVariant,
    };
  }, [currentChildsOnSelectedStage, variants]);

  if (currentChildsOnSelectedStage.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center border-2 border-dashed border-gray-200 rounded-lg bg-gray-50/50">
        <p className="text-sm text-muted-foreground mb-4 font-medium">
          No Deals found
        </p>
        <Button
          variant="outline"
          onClick={handleAddDeal}
          className="gap-2"
          disabled={disabled}
        >
          <PlusCircle className="h-4 w-4" />
          {disabled ? "You are not allowed to add a deal" : "Click to add"}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-[14px] border border-[#d6ebe3] bg-gradient-to-r from-[#f0f9f5] to-[#f7faf8] p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13px] font-semibold text-[#051912]">
            {stageInsights.totalDeals} Deal
            {stageInsights.totalDeals > 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-1">
            <IndianRupee className="size-3.5 text-[#267355]" />
            <span className="text-[15px] font-bold text-[#051912] tabular-nums">
              ₹{fmt(stageInsights.totalValue)}
            </span>
          </div>
        </div>

        {/* Pipeline amounts: Total, Received, Pending */}
        <div className="grid grid-cols-3 gap-2 mb-2">
          <div className="rounded-lg bg-white/70 px-2.5 py-2 text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <IndianRupee className="size-3 text-[#335CFF]" />
              <span className="text-[9px] text-[#9ba3a0] uppercase font-medium">
                Total
              </span>
            </div>
            <p className="text-[13px] font-bold text-[#051912] tabular-nums">
              ₹{fmt(stageInsights.totalValue)}
            </p>
          </div>
          <div className="rounded-lg bg-white/70 px-2.5 py-2 text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <TrendingUp className="size-3 text-[#16a34a]" />
              <span className="text-[9px] text-[#9ba3a0] uppercase font-medium">
                Received
              </span>
            </div>
            <p className="text-[13px] font-bold text-[#16a34a] tabular-nums">
              ₹{fmt(stageInsights.received)}
            </p>
          </div>
          <div className="rounded-lg bg-white/70 px-2.5 py-2 text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <Wallet className="size-3 text-[#EA580C]" />
              <span className="text-[9px] text-[#9ba3a0] uppercase font-medium">
                Pending
              </span>
            </div>
            <p
              className={`text-[13px] font-bold tabular-nums ${stageInsights.pending > 0 ? "text-[#EA580C]" : "text-[#267355]"}`}
            >
              ₹{fmt(stageInsights.pending)}
            </p>
          </div>
        </div>

        {stageInsights.byVariant.length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {stageInsights.byVariant.map((bv, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[12px]">
                <span className="text-[#374741]">{bv.name}</span>
                <span className="text-[#9ba3a0]">×{bv.count}</span>
                <span className="text-[#051912] font-medium tabular-nums">
                  ₹{fmt(bv.subtotal)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <AcademyAccordion />
    </div>
  );
}
